import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatButtonModule } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { TranslateModule } from '@ngx-translate/core'
import { ItemsFacade } from '@galabid/gb-dashboard/item/data-access'
import { GbDashboardItemUiModule } from '@galabid/gb-dashboard/item/ui'
import {
  GbDashboardSharedFeatureModule,
  GbTableColumn,
  GbTableItem,
  ColumnTypeEnum,
  GbTableColumnTemplate,
} from '@galabid/gb-dashboard/shared/feature'
import { combineLatest, distinctUntilChanged, fromEvent, map, of, shareReplay, take } from 'rxjs'
import * as ItemsActions from 'libs/gb-dashboard/item/data-access/src/lib/+state/items.actions'

export interface GbPresenterItemFilterDialogData {
  hiddenItemUuids: string[]
}

export interface GbPresenterItemFilterDialogResult {
  hiddenItemUuids: string[]
}

@Component({
  selector: 'gb-presenter-item-filter-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    TranslateModule,
    GbDashboardSharedFeatureModule,
    GbDashboardItemUiModule,
  ],
  templateUrl: './gb-presenter-item-filter-dialog.component.html',
  styleUrl: './gb-presenter-item-filter-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterItemFilterDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('itemsTableContainer') itemsTableContainer?: ElementRef<HTMLDivElement>
  selectedItems: string[] = []
  hiddenItemUuids: string[] = []

  loaded$ = this.itemsFacade.loaded$
  count$ = this.itemsFacade.count$

  items$ = this.itemsFacade.allItems$.pipe(
    map((items) => (items || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  isLoadingMore = false

  tableData$ = this.items$.pipe(
    map((items): GbTableItem[] =>
      items.map((item) => ({
        uuid: item.uuid,
        name: item.name,
        alias: item.alias,
        item_type: item.item_type,
        bidding_item: item.bidding_item,
        is_visible: item.is_visible,
      })),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  displayedColumns$ = of<GbTableColumn[]>([
    {
      columnKey: 'name',
      columnLabel: 'NAME',
      columnType: ColumnTypeEnum.Label,
    },
    {
      columnKey: 'alias',
      columnLabel: 'ITEMS.IOC_SHOP.ALIAS',
      columnType: ColumnTypeEnum.Label,
      disableSort: true,
    },
    {
      columnKey: 'item_type',
      columnLabel: 'ITEMS.LIST.TYPE',
      columnType: ColumnTypeEnum.Template,
    },
    {
      columnKey: 'is_visible',
      columnLabel: 'PRESENTER.ITEM_TABLE_TOOLS.FILTER_ITEMS.CAMPAIGN_VISIBILITY',
      columnType: ColumnTypeEnum.Visibility,
      disableSort: true,
    },
  ])

  vm$ = combineLatest([this.items$, this.loaded$, this.count$]).pipe(
    map(([items, loaded, count]) => ({ items, loaded, count })),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  constructor(
    private itemsFacade: ItemsFacade,
    private dialogRef: MatDialogRef<GbPresenterItemFilterDialogComponent, GbPresenterItemFilterDialogResult>,
    private destroyRef: DestroyRef,
    @Inject(MAT_DIALOG_DATA) public data: GbPresenterItemFilterDialogData,
  ) {}

  ngOnInit(): void {
    this.hiddenItemUuids = [...(this.data.hiddenItemUuids || [])]

    this.count$.pipe(take(1)).subscribe((count) => {
      if (count === 0) {
        this.itemsFacade.dispatch(ItemsActions.loadItems({ projector: true, limit: 20 }))
      }
    })

    this.items$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((items) => {
      this.syncSelectedItems(items)
    })

    this.loaded$.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe((loaded) => {
      if (loaded) {
        this.isLoadingMore = false
      }
    })
  }

  ngAfterViewInit(): void {
    this.setupInfiniteScroll()
  }

  private setupInfiniteScroll(): void {
    const container = this.itemsTableContainer?.nativeElement
    if (!container) return

    fromEvent(container, 'scroll')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const { scrollTop, scrollHeight, clientHeight } = container
        const scrollThreshold = 100

        if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
          this.loadMoreItems()
        }
      })
  }

  private loadMoreItems(): void {
    if (this.isLoadingMore) return

    combineLatest([this.items$, this.count$, this.loaded$])
      .pipe(take(1))
      .subscribe(([items, count, loaded]) => {
        if (loaded && items.length < count) {
          this.isLoadingMore = true
          this.itemsFacade.dispatch(ItemsActions.loadItems({ projector: true, limit: 20 }))
        }
      })
  }

  getDisplayedColumns$(itemType: TemplateRef<GbTableColumnTemplate>) {
    return this.displayedColumns$.pipe(
      map((columns) =>
        columns.map((column) => (column.columnKey === 'item_type' ? { ...column, columnTemplate: itemType } : column)),
      ),
    )
  }

  private syncSelectedItems(items: Array<{ uuid: string }>): void {
    const hiddenSet = new Set(this.hiddenItemUuids)
    this.selectedItems = items.filter((item) => !hiddenSet.has(item.uuid)).map((item) => item.uuid)
  }

  onSelectedItemsChange(selectedItems: string[]): void {
    this.items$.pipe(take(1)).subscribe((items) => {
      const hiddenSet = new Set(this.hiddenItemUuids)

      items.forEach((item) => {
        if (selectedItems.includes(item.uuid)) {
          hiddenSet.delete(item.uuid)
        } else {
          hiddenSet.add(item.uuid)
        }
      })

      this.hiddenItemUuids = [...hiddenSet]
      this.syncSelectedItems(items)
    })
  }

  cancel(): void {
    this.dialogRef.close()
  }

  apply(): void {
    this.dialogRef.close({ hiddenItemUuids: this.hiddenItemUuids })
  }
}
